import React from 'react';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';
import { cl } from 'lincd/lib/utils/ClassNames';
import { Icons } from '../Icons';
import { Tooltip } from '../Tooltip';
import { Table } from '../Table';
import style from '../../pages/Automations.scss.json';
import { ROUTES } from '../../routes';

export interface AdsAutomationHeaderProps {
  automation: { name: string; alternateName?: string; description: string };
  lastExecuted: { currentTime: string; lastExecuted: string } | null;
  onRetrieve: () => void;
  loading: boolean;
  infoRules: string[];
  retrieveButtonLabel?: string;
}

export const AdsAutomationHeader: React.FC<AdsAutomationHeaderProps> = ({
  automation,
  lastExecuted,
  onRetrieve,
  loading,
  infoRules,
  retrieveButtonLabel = 'Retrieve Ads',
}) => {
  return (
    <div className={cl(style.headerContent)}>
      <h3>
        {automation.name} {automation.alternateName ? `(${automation.alternateName})` : ''}
      </h3>
      <p>{automation.description}</p>
      <button className={style.btn} disabled={loading} onClick={onRetrieve}>
        {loading ? 'Retrieving...' : retrieveButtonLabel}
      </button>
      {lastExecuted && (
        <p className={style.lastExecuted}>
          Last Executed:{' '}
          <span>{dayjs(lastExecuted.lastExecuted).format('MMMM D, YYYY h:mm A')} PST</span>
          <br />
          Current Time:{' '}
          <span>{dayjs(lastExecuted.currentTime).format('MMMM D, YYYY h:mm A')} PST</span>
          <br />
        </p>
      )}
      <div className={style.info}>
        <h4>This will pause all currently running ads EXCEPT the following ads:</h4>
        <ol>
          {infoRules.map((rule, index) => (
            <li key={index}>{rule}</li>
          ))}
        </ol>
      </div>
    </div>
  );
};

export interface AdsKeepRunningTableProps {
  ads: { uri: string; shortName: string; quoraId: string }[];
  adsKeepRunningReasons: Record<
    string,
    {
      rule: string;
      message: string;
    }[]
  >;
}

export const AdsKeepRunningTable: React.FC<AdsKeepRunningTableProps> = ({
  ads,
  adsKeepRunningReasons,
}) => {
  const today = dayjs().format('YYYY-MM-DD');
  return (
    <div className={style.tableBlock}>
      <h5>Keep Running: {ads.length} Ads</h5>
      <div className={cl(style.tableWrapper, style.withoutScroll)}>
        <Table.Root isStickyHeader>
          <Table.Header>
            <Table.Row>
              <Table.Head className={style.cellFull}>Ad Name</Table.Head>
              <Table.Head className={style.cellAction}>Rules</Table.Head>
              <Table.Head className={style.cellAction}>DPT</Table.Head>
              <Table.Head className={style.cellAction}>Quora</Table.Head>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {ads.map((ad) => {
              const dptLink = `${ROUTES.dayPartingTrackerDetail.path}?uri=${ad.uri}&date=${today}`;
              const quoraLink = `https://www.quora.com/ads/ad_performance?id=${ad.quoraId}`;
              return (
                <Table.Row key={ad.uri}>
                  <Table.Cell className={style.cellFull}>{ad.shortName}</Table.Cell>
                  <Table.Cell className={style.cellAction}>
                    <Tooltip.Provider>
                      <Tooltip.Root>
                        <Tooltip.Trigger className={style.infoCircle}>
                          <Icons.InfoCircled width={16} height={16} />
                        </Tooltip.Trigger>
                        <Tooltip.Content>
                          <ul>
                            {adsKeepRunningReasons[ad.uri]?.map((reason, index) => (
                              <li key={index}>{reason.message}</li>
                            ))}
                          </ul>
                        </Tooltip.Content>
                      </Tooltip.Root>
                    </Tooltip.Provider>
                  </Table.Cell>
                  <Table.Cell className={style.cellAction}>
                    <Link to={dptLink} target="_blank">
                      <Icons.Link2 />
                    </Link>
                  </Table.Cell>
                  <Table.Cell className={style.cellAction}>
                    <Link to={quoraLink} target="_blank">
                      <Icons.Link2 />
                    </Link>
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table.Root>
      </div>
    </div>
  );
};

export interface AdsToPauseTableProps {
  ads: { uri: string; shortName: string; quoraId: string }[];
  selectedAds: Set<string>;
  onCheckboxChange: (uri: string) => void;
  onSelectAllChange: () => void;
  selectAll: boolean;
}

export const AdsToPauseTable: React.FC<AdsToPauseTableProps> = ({
  ads,
  selectedAds,
  onCheckboxChange,
  onSelectAllChange,
  selectAll,
}) => {
  const today = dayjs().format('YYYY-MM-DD');
  return (
    <div className={style.tableBlock} style={{ border: 0 }}>
      <h5>Will Be Paused: {ads.length} Ads</h5>
      <div className={cl(style.tableWrapper)}>
        <Table.Root isStickyHeader>
          <Table.Header>
            <Table.Row>
              <Table.Head className={style.cellCheckbox}>
                <input type="checkbox" checked={selectAll} onChange={onSelectAllChange} />
              </Table.Head>
              <Table.Head className={style.cellFull}>Ad Name</Table.Head>
              <Table.Head className={style.cellAction}>DPT</Table.Head>
              <Table.Head className={style.cellAction}>Quora</Table.Head>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {ads.map((ad) => {
              const dptLink = `${ROUTES.dayPartingTrackerDetail.path}?uri=${ad.uri}&date=${today}`;
              const quoraLink = `https://www.quora.com/ads/ad_performance?id=${ad.quoraId}`;
              return (
                <Table.Row key={ad.uri}>
                  <Table.Cell>
                    <input
                      type="checkbox"
                      checked={selectedAds.has(ad.uri)}
                      onChange={() => onCheckboxChange(ad.uri)}
                    />
                  </Table.Cell>
                  <Table.Cell className={style.cellFull}>{ad.shortName}</Table.Cell>
                  <Table.Cell className={style.cellAction}>
                    <Link to={dptLink} target="_blank">
                      <Icons.Link2 />
                    </Link>
                  </Table.Cell>
                  <Table.Cell className={style.cellAction}>
                    <Link to={quoraLink} target="_blank">
                      <Icons.Link2 />
                    </Link>
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table.Root>
      </div>
    </div>
  );
};
